// SPDX-License-Identifier: GPL-3.0-or-later
//
// In-memory cache of the manifest + write-through to disk + multi-tab sync
// via BroadcastChannel.

import { readManifest, writeManifest } from '../fs/manifest';
import { buildFolderRelPath, sanitizeFilename } from '../fs/util';
import {
  DEFAULT_CONFIG,
  type Application,
  type GenRef,
  type Manifest,
  type ManifestConfig,
  type Status,
} from '../types';
import type { DocKind } from '../generate';

const CHANNEL_NAME = 'applify';

type ChannelMessage =
  | { type: 'manifest-changed'; origin: string }
  | { type: 'root-changed'; origin: string };

let current: Manifest | null = null;
let currentRoot: FileSystemDirectoryHandle | null = null;
const tabId = crypto.randomUUID();
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) fn();
}

function broadcast(msg: ChannelMessage): void {
  channel?.postMessage(msg);
}

channel?.addEventListener('message', (e: MessageEvent<ChannelMessage>) => {
  if (e.data.origin === tabId) return;
  if (e.data.type === 'manifest-changed' && currentRoot) {
    void reloadManifest();
  }
});

async function reloadManifest(): Promise<void> {
  if (!currentRoot) return;
  const m = await readManifest(currentRoot);
  current = m ?? blankManifest();
  notify();
}

function blankManifest(): Manifest {
  return { schemaVersion: 2, applications: [], config: { ...DEFAULT_CONFIG } };
}

// Loads from disk and updates the cache. Does NOT call notify() — the caller
// drove this load and will render itself. notify() is reserved for mutations
// and out-of-band updates (BroadcastChannel from another tab).
export async function loadManifest(root: FileSystemDirectoryHandle): Promise<Manifest> {
  currentRoot = root;
  const m = await readManifest(root);
  current = m ?? blankManifest();
  return current;
}

export function getManifest(): Manifest | null {
  return current;
}

export function getRoot(): FileSystemDirectoryHandle | null {
  return currentRoot;
}

async function persist(next: Manifest): Promise<void> {
  if (!currentRoot) throw new Error('No root selected');
  current = next;
  notify();
  await writeManifest(currentRoot, next);
  broadcast({ type: 'manifest-changed', origin: tabId });
}

export async function updateStatus(id: string, status: Status): Promise<void> {
  if (!current) throw new Error('Manifest not loaded');
  const next: Manifest = {
    ...current,
    applications: current.applications.map((a) =>
      a.id === id ? { ...a, status } : a,
    ),
  };
  await persist(next);
}

export async function updateApplicationFields(
  id: string,
  patch: Partial<Pick<Application, 'status' | 'notes'>>,
): Promise<void> {
  if (!current) throw new Error('Manifest not loaded');
  const next: Manifest = {
    ...current,
    applications: current.applications.map((a) =>
      a.id === id ? { ...a, ...patch } : a,
    ),
  };
  await persist(next);
}

// Record a generated document (CV / cover / interview) on an application.
// Updates the in-memory manifest cache + persists to disk + broadcasts to
// other tabs in one go. Used by generate.ts.
export async function setGeneratedRef(
  id: string,
  kind: DocKind,
  ref: GenRef,
): Promise<void> {
  if (!current) throw new Error('Manifest not loaded');
  const next: Manifest = {
    ...current,
    applications: current.applications.map((a) =>
      a.id === id
        ? { ...a, generated: { ...(a.generated ?? {}), [kind]: ref } }
        : a,
    ),
  };
  await persist(next);
}

export async function updateConfig(patch: Partial<ManifestConfig>): Promise<void> {
  if (!current) throw new Error('Manifest not loaded');
  const next: Manifest = {
    ...current,
    config: { ...current.config, ...patch },
  };
  await persist(next);
}

export interface JobPayload {
  jobTitle: string;
  companyName: string;
  jobUrl: string;
  applyType: string;
  description: string;
}

export interface AddResult {
  application: Application;
  created: boolean;
}

export async function addApplication(payload: JobPayload): Promise<AddResult> {
  if (!currentRoot) throw new Error('No folder selected');
  if (!current) throw new Error('Manifest not loaded');

  const existing = current.applications.find((a) => a.jobUrl === payload.jobUrl);
  if (existing) return { application: existing, created: false };

  const folderRelPath = await buildFolderRelPath(currentRoot, payload.companyName);
  const folder = await currentRoot.getDirectoryHandle(folderRelPath, { create: true });

  const txtName = `${sanitizeFilename(payload.jobTitle) || 'Job'}.txt`;
  const fileHandle = await folder.getFileHandle(txtName, { create: true });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(`${payload.jobUrl}\n${payload.applyType}\n${payload.description}`);
  } finally {
    await writable.close();
  }

  const application: Application = {
    id: crypto.randomUUID(),
    jobTitle: payload.jobTitle,
    companyName: payload.companyName,
    jobUrl: payload.jobUrl,
    applyType: payload.applyType,
    status: 'Saved',
    savedAt: new Date().toISOString(),
    folderRelPath,
  };

  const next: Manifest = {
    ...current,
    applications: [application, ...current.applications],
  };
  await persist(next);
  return { application, created: true };
}

export async function removeApplication(id: string): Promise<void> {
  if (!current) throw new Error('Manifest not loaded');
  const next: Manifest = {
    ...current,
    applications: current.applications.filter((a) => a.id !== id),
  };
  await persist(next);
}

// Test helper used by tests / dev tools; not exported through the index.
export function _reset(): void {
  current = null;
  currentRoot = null;
  listeners.clear();
}

export type { Application };
